<template>
    <div>
        <BardToUnlayerMigrationPreview
            v-if="showConvertModal"
            :cell-name="cell.value.name"
            :funnel-id="funnelId"
            :marketing-email-id="Number(cell.value.marketingEmailId.toString().replace('L', ''))"
            @close="handleMigrationPreviewModalClose"
        />

        <DsConfirmDialog
            v-else
            :is-open="isOpen"
            :title="$t('funnel.edit.bardEmail.unsupported.title')"
            :confirm-label="$t('global.learnMore')"
            :cancel-label="$t('global.cancel')"
            @cancel="closeModal"
            @confirm="learnMore"
        >
            <template #message>
                <div class="unsupported-text-description">
                    {{ $t('funnel.edit.bardEmail.unsupported.text') }}
                    <DsLink as="button" data-qa="convert-to-unlayer-link" @click="initBardToUnlayerMigration">
                        {{ $t('funnel.edit.bardEmail.unsupported.link') }}
                    </DsLink>
                </div>
            </template>
        </DsConfirmDialog>
    </div>
</template>

<script>
import { PROCESS_UNLAYER_EMAIL } from '../../../../utils/editor.constants';
import BardToUnlayerMigrationPreview from '@/components/unlayer/BardToUnlayerMigrationPreview.vue';
import sentry from '@/analytics/sentry';

export default {
    components: {
        BardToUnlayerMigrationPreview,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        funnelId: {
            type: Number,
            required: true,
        },
        cell: {
            type: Object,
            required: true,
        },
        cells: {
            type: Array,
            required: true,
        },
        automaticallyConvertBard: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['close-modal', 'migrated-bard-to-unlayer', 'update-cell-value'],

    data() {
        return {
            showConvertModal: false,
        };
    },

    mounted() {
        if (this.automaticallyConvertBard) {
            this.initBardToUnlayerMigration();
        }
    },

    methods: {
        learnMore() {
            window.open('https://help.keap.com/help/how-to-convert-legacy-emails-into-the-new-builder', '_blank', 'noopener,noreferrer');
        },

        closeModal() {
            this.$emit('close-modal');
        },

        initBardToUnlayerMigration() {
            if (!this.cell.value.marketingEmailId) {
                this.finalizeBardToUnlayerMigration();
            } else {
                this.showConvertModal = true;
            }
        },

        finalizeBardToUnlayerMigration(marketingEmailId) {
            try {
                const { cell } = this;
                const updatedCell = {
                    ...cell,
                    type: PROCESS_UNLAYER_EMAIL,
                    style: PROCESS_UNLAYER_EMAIL,
                    meta: {
                        ...cell.meta,
                        style: PROCESS_UNLAYER_EMAIL,
                    },
                    value: {
                        ...cell.value,
                        marketingEmailId,
                    },
                };

                this.showConvertModal = false;

                this.$emit('migrated-bard-to-unlayer', { cell: updatedCell, isReady: false });
            } catch (error) {
                sentry.error(error);
                throw error;
            }
        },

        handleMigrationPreviewModalClose({ converted, marketingEmailId, linkRelationshipList }) {
            if (!converted) {
                this.showConvertModal = false;
                this.closeModal();

                return;
            }

            if (linkRelationshipList?.length && this.cells) {
                const linkClickCells = this.cells.filter((cell) => cell?.type === 'linkClick' && cell?.value?.linkIds !== undefined);

                const simpleLinkRelationshipList = linkRelationshipList.reduce((acc, current) => {
                    acc[current.bardlink] = current.unlayerlink;

                    return acc;
                }, {});

                linkClickCells.forEach((cell) => this.updateLinkIdsInCell(cell, simpleLinkRelationshipList));
            }

            this.finalizeBardToUnlayerMigration(marketingEmailId);
        },

        updateLinkIdsInCell(cell, linkRelationshipList) {
            try {
                const updatedLinks = cell.value.linkIds.map((linkId) => {
                    const currentId = linkId.replace(/\D/g, '');

                    return linkRelationshipList[currentId] !== undefined
                        ? linkId.replace(currentId, linkRelationshipList[currentId])
                        : linkId;
                });

                const updatedCell = { ...cell };

                updatedCell.value.linkIds = updatedLinks;

                this.$emit('update-cell-value', { cell: updatedCell, isReady: false });
            } catch (error) {
                sentry.captureException(error);
            }
        },

    },
};

</script>

<style lang="scss" scoped>
.unsupported-text-description {
    white-space: pre-line;
    font-size: $font-size-sm;
}
</style>
